import { SIDEBAR_WIDTH } from "@/constants";
import type { GameSpecifications } from "@/utils/cms/getGameSpecs";
import {
  Box,
  Heading,
  HeroBackground,
  Stack,
  mediaQuerySizesHelper,
} from "@biom3/react";
import { SliceContext } from "@cms/context";
import { components } from "@cms/slices";
import type { Content } from "@prismicio/client";
import { SliceZone } from "@prismicio/react";
import { GameHeader, GameSpecs, type WishlistProps } from "@ui-kit";
import { UnsubscribeFooter } from "@ui-kit/Unsubscribe";
import {
  useIsLessThanLargeScreen,
  useIsLessThanMediumScreen,
  useIsLessThanXLargeScreen,
} from "@ui-kit/hooks/screenSizeHooks";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type StandardGameDetailsLayoutProps = {
  defaultBgImage: string | null | undefined;
  game: Content.GameDocument;
  page: Content.GameDetailsPageDocument;
  gameSpecs: GameSpecifications;
  wishlistProps: WishlistProps;
};

export function StandardGameDetailsLayout({
  defaultBgImage,
  game,
  page,
  gameSpecs,
  wishlistProps,
}: StandardGameDetailsLayoutProps) {
  const isSmallScreenMode = useIsLessThanMediumScreen();
  const isMediumScreenMode = useIsLessThanLargeScreen();
  const isLargeScreenMode = useIsLessThanXLargeScreen();

  const { t } = useTranslation();
  const [backgroundImg, setBackgroundImg] = useState<string | null | undefined>(
    defaultBgImage,
  );
  const gameContext = useMemo(() => ({ game }), [game]);

  const RenderTitle = (
    <>
      {isSmallScreenMode ||
        isMediumScreenMode ||
        (isLargeScreenMode && (
          <Box sx={{ mt: "base.spacing.x6" }}>
            <Heading size={"large"} weight="bold">
              {page.data.title}
            </Heading>
          </Box>
        ))}
    </>
  );

  return (
    <>
      {backgroundImg && (
        <HeroBackground
          sx={{
            pos: "absolute",
            top: "0",
            left: "0",
            bradtl: "base.borderRadius.x8",
            bradtr: "base.borderRadius.x8",
          }}
          aspectRatio={["9/16", null, null, null, "16/9"]}
        >
          <HeroBackground.Image
            imageUrl={backgroundImg}
            loading="eager"
            responsiveSizes={[
              256, 512, 1024, 1280, 1920, 2160, 2304, 2560, 2732, 2880, 3072,
              3200, 3456, 3840, 4096,
            ]}
            relativeImageSizeInLayout={mediaQuerySizesHelper([
              { rule: "default", width: "100dvw - 16px" },
              {
                rule: "minWidth",
                breakpoint: "medium",
                width: `calc(100dvw - ${SIDEBAR_WIDTH} - 32px)`,
              },
            ])}
          />
        </HeroBackground>
      )}
      <Stack
        gap={"base.spacing.x10"}
        sx={{
          pos: "relative",
          px: isSmallScreenMode ? "base.spacing.x4" : "base.spacing.x14",
          py: isSmallScreenMode ? "base.spacing.x4" : "base.spacing.x16",
        }}
        testId="GamesDetailPage__mainContainer"
      >
        <Box sx={{ display: "flex", justifyContent: "left" }}>
          <GameHeader title={page.data.title} game={game} />
        </Box>
        <Stack
          direction={["column", null, null, null, "row"]}
          alignItems={["stretch", null, null, null, "flex-start"]}
          gap="base.spacing.x12"
          testId="GamesDetailsPage"
        >
          <Box
            sx={{ w: ["100%", null, null, null, "70%", "75%"] }}
            testId="GamesDetailPage__main"
          >
            <SliceContext.Provider
              value={{
                t,
                wishlistProps,
                data: {
                  game,
                  mediaCarousel: {
                    onSlideChange: (url: string) => setBackgroundImg(url),
                  },
                },
              }}
            >
              <SliceZone
                components={components}
                slices={page.data.slices}
                context={gameContext}
              />
            </SliceContext.Provider>
          </Box>
          <Box
            sx={{
              position: ["static", null, null, null, "sticky"],
              top: "0",
              w: ["100%", null, null, null, "25%"],
              order: ["-1", null, null, null, 1],
            }}
            testId="GamesDetailPage__rightSide"
          >
            <GameSpecs
              game={game}
              gameSpec={gameSpecs}
              wishlistProps={wishlistProps}
            />
            {RenderTitle}
          </Box>
        </Stack>
      </Stack>

      <UnsubscribeFooter />
    </>
  );
}
